import api from "./api";

// ---- Video Mode & Chapter Types ----

export type QuizVideoMode = "none" | "full" | "chapters";

export type VideoChapter = {
  title: string;
  description?: string;
  start_time: number; // seconds
  end_time: number;   // seconds
};

export type Quiz = {
  id: string;
  title: string;
  description: string | null;
  // Optional rich-text learning content shown before the quiz (no scoring impact).
  content?: string | null;
  topic: string;
  difficulty: "easy" | "medium" | "hard";
  category: "nutrition" | "training" | "mental_health" | "sports_science" | "rules" | "safety" | "general";
  theme_id: string | null;
  // Theme the quiz belongs to (from the list endpoint's join). Null when the
  // quiz isn't attached to any theme.
  theme?: { id: string; title: string } | null;
  sport_type: string | null;
  age_group: string | null;
  /** Vârstele pentru care e scris capitolul; null = toate. */
  age_min?: number | null;
  age_max?: number | null;
  is_active: boolean;
  reward_points: number;
  // Real max earnable = sum of question points (shown as "up to +X").
  max_points?: number;
  created_at: string;
  updated_at: string;
  question_count?: number;
  attempt_count?: number;
  // Video fields (quiz-level)
  video_url?: string | null;
  video_mode?: QuizVideoMode;
  video_duration?: number | null;
  video_chapters?: VideoChapter[] | null;
};

export type QuizQuestion = {
  id: string;
  quiz_id: string;
  text: string;
  media_type: "text" | "image" | "video" | null;
  media_url: string | null;
  video_duration: number | null;
  kind: "single" | "multi" | "true_false";
  options: any;
  correct_answer: any;
  order_no: number;
  // Points fields
  points: number;
  // Per-question answer timer (seconds). Null falls back to the quiz timer.
  time_limit_seconds?: number | null;
  chapter_index: number | null;
  explanation?: string | null;
  // Only on the questions served when an attempt starts, multi kind only.
  correct_count?: number | null;
};

export type QuizWithQuestions = Quiz & {
  questions: QuizQuestion[];
};

export type QuizzesListResponse = {
  total: number;
  page: number;
  pageSize: number;
  data: Quiz[];
};

/**
 * List all quizzes with filters
 */
export async function getQuizzes(params?: {
  category?: string;
  sport_type?: string;
  age_group?: string;
  difficulty?: string;
  themeId?: string;
  search?: string;
  is_active?: boolean;
  has_questions?: boolean;
  page?: number;
  pageSize?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.category) queryParams.append("category", params.category);
  if (params?.sport_type) queryParams.append("sport_type", params.sport_type);
  if (params?.age_group) queryParams.append("age_group", params.age_group);
  if (params?.difficulty) queryParams.append("difficulty", params.difficulty);
  if (params?.themeId) queryParams.append("themeId", params.themeId);
  if (params?.search) queryParams.append("search", params.search);
  if (params?.is_active !== undefined) queryParams.append("is_active", String(params.is_active));
  if (params?.has_questions !== undefined) queryParams.append("has_questions", String(params.has_questions));
  if (params?.page) queryParams.append("page", String(params.page));
  if (params?.pageSize) queryParams.append("pageSize", String(params.pageSize));

  const qs = queryParams.toString();
  const { data } = await api.get<QuizzesListResponse>(`/quizzes${qs ? `?${qs}` : ""}`);
  return data;
}

// Aggregate counters for the admin quiz browser.
export interface QuizAdminStats {
  views: { all: number; active: number; inactive: number; no_questions: number };
  by_category: Record<string, number>;
}

export async function getQuizAdminStats(params?: {
  category?: string;
  sport_type?: string;
  age_group?: string;
  difficulty?: string;
  search?: string;
}) {
  const qp = new URLSearchParams();
  if (params?.category) qp.append("category", params.category);
  if (params?.sport_type) qp.append("sport_type", params.sport_type);
  if (params?.age_group) qp.append("age_group", params.age_group);
  if (params?.difficulty) qp.append("difficulty", params.difficulty);
  if (params?.search) qp.append("search", params.search);
  const qs = qp.toString();
  const { data } = await api.get<QuizAdminStats>(`/quizzes/admin-stats${qs ? `?${qs}` : ""}`);
  return data;
}

/**
 * Get quiz by ID with all questions
 */
export async function getQuizById(quizId: string) {
  const { data } = await api.get<QuizWithQuestions>(`/quizzes/${quizId}`);
  return data;
}

/**
 * Create new quiz
 */
export async function createQuiz(payload: {
  title: string;
  description?: string;
  content?: string;
  topic: string;
  difficulty?: "easy" | "medium" | "hard";
  category?: string;
  theme_id?: string;
  theme_order?: number | null;
  sport_type?: string | null;
  age_group?: string | null;
  age_min?: number | null;
  age_max?: number | null;
  reward_points?: number;
  is_active?: boolean;
  free_included?: boolean;
  // Video fields
  video_url?: string;
  video_mode?: QuizVideoMode;
  video_duration?: number;
  video_chapters?: VideoChapter[];
}) {
  const { data } = await api.post<Quiz>("/quizzes", payload);
  return data;
}

/**
 * Update quiz
 */
export async function updateQuiz(
  quizId: string,
  payload: {
    title?: string;
    description?: string;
    content?: string;
    topic?: string;
    difficulty?: "easy" | "medium" | "hard";
    category?: string;
    theme_id?: string | null;
    theme_order?: number | null;
    sport_type?: string | null;
    age_group?: string | null;
  age_min?: number | null;
  age_max?: number | null;
    reward_points?: number;
    is_active?: boolean;
    free_included?: boolean;
    // Video fields
    video_url?: string | null;
    video_mode?: QuizVideoMode;
    video_duration?: number | null;
    video_chapters?: VideoChapter[] | null;
  }
) {
  const { data } = await api.patch<Quiz>(`/quizzes/${quizId}`, payload);
  return data;
}

/**
 * Delete quiz
 */
export async function deleteQuiz(quizId: string) {
  const { data } = await api.delete(`/quizzes/${quizId}`);
  return data;
}

/**
 * Add question to quiz
 */
export async function addQuestion(
  quizId: string,
  payload: {
    text: string;
    media_type?: "text" | "image" | "video";
    media_url?: string;
    video_duration?: number;
    kind: "single" | "multi" | "true_false";
    options: any;
    correct_answer: any;
    order_no?: number;
    // Points fields
    points?: number;
    time_limit_seconds?: number | null;
    chapter_index?: number;
    explanation?: string | null;
  }
) {
  const { data } = await api.post<QuizQuestion>(`/quizzes/${quizId}/questions`, payload);
  return data;
}

/**
 * Update question
 */
export async function updateQuestion(
  quizId: string,
  questionId: string,
  payload: {
    text?: string;
    media_type?: "text" | "image" | "video";
    media_url?: string;
    video_duration?: number;
    kind?: "single" | "multi" | "true_false";
    options?: any;
    correct_answer?: any;
    order_no?: number;
    // Points fields
    points?: number;
    time_limit_seconds?: number | null;
    chapter_index?: number | null;
    explanation?: string | null;
  }
) {
  const { data } = await api.patch<QuizQuestion>(`/quizzes/${quizId}/questions/${questionId}`, payload);
  return data;
}

/**
 * Delete question
 */
export async function deleteQuestion(quizId: string, questionId: string) {
  const { data} = await api.delete(`/quizzes/${quizId}/questions/${questionId}`);
  return data;
}

// ---- PHASE 2: Quiz Assignments ----

export type QuizAssignment = {
  id: string;
  quiz_id: string;
  age_group: string;
  quizzes_per_week: number;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type AssignedQuiz = {
  assignment: {
    id: string;
    quizzes_per_week: number;
    start_date: string;
    end_date: string | null;
  };
  quiz: Quiz & {
    question_count: number;
    user_attempt_count: number;
  };
};

/**
 * Get assignments for a quiz (admin)
 */
export async function getQuizAssignments(quizId: string) {
  const { data } = await api.get<QuizAssignment[]>(`/quizzes/${quizId}/assignments`);
  return data;
}

/**
 * Create quiz assignment (admin assigns to age group)
 */
export async function createQuizAssignment(
  quizId: string,
  payload: {
    age_group: string;
    quizzes_per_week?: number;
    start_date: string;
    end_date?: string;
    is_active?: boolean;
  }
) {
  const { data } = await api.post<QuizAssignment>(`/quizzes/${quizId}/assignments`, payload);
  return data;
}

/**
 * Delete quiz assignment
 */
export async function deleteQuizAssignment(quizId: string, assignmentId: string) {
  const { data } = await api.delete(`/quizzes/${quizId}/assignments/${assignmentId}`);
  return data;
}

/**
 * Get quizzes assigned to current user (based on age group)
 */
export async function getAssignedQuizzes() {
  const { data } = await api.get<AssignedQuiz[]>("/quizzes/assigned/me");
  return data;
}

// ── Per-question analytics (admin) ─────────────────────────────────
export type QuizAnalytics = {
  quiz: {
    id: string;
    title: string;
    total_attempts: number;
    completed_attempts: number;
    total_answers: number;
    overall_accuracy_pct: number | null;
  };
  questions: {
    id: string;
    text: string;
    kind: "single" | "multi" | "true_false";
    order_no: number;
    points: number;
    times_answered: number;
    correct_count: number;
    p_value: number | null; // % correct
    avg_response_ms: number | null;
    distribution: { label: string; count: number; is_correct: boolean }[];
    flag: "healthy" | "hard" | "easy" | "ambiguous" | "low_data";
  }[];
};

export async function getQuizAnalytics(quizId: string) {
  const { data } = await api.get<QuizAnalytics>(`/quizzes/${quizId}/analytics`);
  return data;
}

/**
 * Weekly quiz response — single quiz per week, team-synchronized
 */
export type WeeklyQuizResponse = {
  current_quiz: (Quiz & {
    question_count: number;
    is_completed: boolean;
    attempt_count: number;
    best_score?: number;
    theme?: { id: string; title: string } | null;
  }) | null;
  week_start: string | null;
  week_end: string | null;
  total_quizzes: number;
  completed_count: number;
  quiz_index: number;
  quiz_history: Array<{
    quiz_id: string;
    title: string;
    completed_at: string;
    score: number;
  }>;
};

/**
 * Get current week's quiz for athlete (team-synchronized algorithm)
 */
export async function getWeeklyQuiz() {
  const { data } = await api.get<WeeklyQuizResponse>("/quizzes/weekly");
  return data;
}

// ---- PHASE 2: Quiz Attempts ----

export type QuizAttempt = {
  id: string;
  quiz_id: string;
  user_id: string;
  answers: Record<string, any>;
  score: number;
  max_score: number;
  completed: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  points_earned?: number;
};

/**
 * Retake info response
 */
export type RetakeInfo = {
  quiz_id: string;
  quiz_title: string;
  is_retake: boolean;
  attempt_count: number;
  retake_deduction: number;
  best_score: number;
  previous_attempts: {
    id: string;
    score: number;
    awarded_points: number;
    completed_at: string;
    correct_count: number;
    wrong_count: number;
  }[];
};

/**
 * Get retake information for a quiz
 * Returns whether this will be a retake and the point deduction cost
 */
export async function getRetakeInfo(quizId: string) {
  const { data } = await api.get<RetakeInfo>(`/quizzes/${quizId}/retake-info`);
  return data;
}

/**
 * A shuffled question as returned by POST /attempts. Options are
 * ALREADY permuted server-side using the per-attempt shuffle order,
 * so the frontend renders them in the order received and sends back
 * `selected_position` (the position it clicked, not the real index).
 */
export type ShuffledAttemptQuestion = {
  id: string;
  text: string;
  kind: "single" | "multi" | "true_false";
  media_type?: string | null;
  media_url?: string | null;
  video_duration?: number | null;
  options: any[];
  order_no: number;
  points: number;
  chapter_index?: number | null;
  // Multi-answer questions: how many options are correct (a count, never which).
  correct_count?: number | null;
  auto_preview_ms: number; // fallback preview time if quiz.preview_seconds is null
};

export type QuizAttemptStartResponse = QuizAttempt & {
  questions: ShuffledAttemptQuestion[];
  quiz_meta: {
    time_limit_seconds: number | null;
    preview_seconds: number | null;
  };
};

/**
 * Start a new quiz attempt. Response now includes shuffled_questions
 * and quiz-level timer / preview meta so the client can render the
 * two-phase reading + answering flow.
 */
export async function startQuizAttempt(quizId: string) {
  const { data } = await api.post<QuizAttemptStartResponse>(`/quizzes/${quizId}/attempts`);
  return data;
}

/**
 * Signal the server that the athlete tapped "Am înțeles" — starts
 * the countdown for this question. Response carries the timer window
 * and the server-side stamp used to validate response_ms on submit.
 */
export async function readyForQuestion(
  quizId: string,
  attemptId: string,
  questionId: string
) {
  const { data } = await api.post<{ answer_started_at: string; timer_ms: number }>(
    `/quizzes/${quizId}/attempts/${attemptId}/questions/${questionId}/ready`
  );
  return data;
}

/**
 * Submit quiz attempt with answers
 */
export async function submitQuizAttempt(
  quizId: string,
  attemptId: string,
  answers: Record<string, any>
) {
  const { data } = await api.patch<QuizAttempt>(`/quizzes/${quizId}/attempts/${attemptId}/submit`, {
    answers,
  });
  return data;
}

/**
 * Get user's attempts for a quiz
 */
export async function getMyQuizAttempts(quizId: string) {
  const { data } = await api.get<QuizAttempt[]>(`/quizzes/${quizId}/attempts/me`);
  return data;
}

// ---- PHASE 2: Video Watch Tracking ----

export type VideoWatchProgress = {
  id?: string;
  question_id?: string;
  user_id?: string;
  watch_progress: number;
  total_duration: number;
  completed: boolean;
  last_watched_at?: string;
};

/**
 * Update video watch progress for a question
 */
export async function updateVideoWatchProgress(
  questionId: string,
  payload: {
    watch_progress: number;
    total_duration: number;
    completed?: boolean;
  }
) {
  const { data } = await api.post<VideoWatchProgress>(
    `/quizzes/questions/${questionId}/watch-progress`,
    payload
  );
  return data;
}

/**
 * Get video watch progress for a question
 */
export async function getVideoWatchProgress(questionId: string) {
  const { data } = await api.get<VideoWatchProgress>(
    `/quizzes/questions/${questionId}/watch-progress`
  );
  return data;
}

// ---- PHASE 3: Leaderboard ----

export type LeaderboardEntry = {
  user_id: string;
  name: string;
  team: string | null;
  age_group: string | null;
  /** Vârstele pentru care e scris capitolul; null = toate. */
  age_min?: number | null;
  age_max?: number | null;
  club: string | null;
  total_score: number;
  quiz_count: number;
};

export type QuizLeaderboardEntry = {
  rank: number;
  user_id: string;
  name: string;
  team: string | null;
  age_group: string | null;
  /** Vârstele pentru care e scris capitolul; null = toate. */
  age_min?: number | null;
  age_max?: number | null;
  club: string | null;
  score: number;
  max_score: number;
  percentage: number;
  completed_at: string | null;
};

/**
 * Get overall leaderboard (top scorers across all quizzes)
 */
export type LeaderboardWindow = "daily" | "weekly" | "monthly" | "yearly" | "alltime";

export async function getLeaderboard(params?: {
  age_group?: string;
  /** Restrict the ranking to one club's athletes. */
  club_id?: string;
  /** Restrict it further to one team. Requires club_id. */
  team_id?: string;
  /** Time window; defaults to all time, as this endpoint always has. */
  period?: LeaderboardWindow;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.age_group) queryParams.append("age_group", params.age_group);
  if (params?.club_id) queryParams.append("club_id", params.club_id);
  if (params?.team_id) queryParams.append("team_id", params.team_id);
  if (params?.period) queryParams.append("period", params.period);
  if (params?.limit) queryParams.append("limit", String(params.limit));

  const qs = queryParams.toString();
  const { data } = await api.get<LeaderboardEntry[]>(
    `/quizzes/leaderboard/all${qs ? `?${qs}` : ""}`
  );
  return data;
}

/**
 * Get leaderboard for a specific quiz
 */
export async function getQuizLeaderboard(quizId: string, limit?: number) {
  const queryParams = new URLSearchParams();
  if (limit) queryParams.append("limit", String(limit));

  const qs = queryParams.toString();
  const { data } = await api.get<QuizLeaderboardEntry[]>(
    `/quizzes/${quizId}/leaderboard${qs ? `?${qs}` : ""}`
  );
  return data;
}

// ---- PHASE 4: Quiz-Level Video Progress ----

export type QuizVideoProgress = {
  id?: string;
  quiz_id: string;
  user_id: string;
  watch_progress: number;    // seconds watched
  chapter_index: number;      // current chapter (0-indexed)
  completed: boolean;
  last_watched_at?: string;
  // Quiz video info (included from response)
  video_mode?: QuizVideoMode;
  video_duration?: number | null;
  video_chapters?: VideoChapter[] | null;
};

/**
 * Get video watch progress for a quiz (quiz-level video)
 */
export async function getQuizVideoProgress(quizId: string) {
  const { data } = await api.get<QuizVideoProgress>(
    `/quizzes/${quizId}/video-progress`
  );
  return data;
}

/**
 * Update video watch progress for a quiz (quiz-level video)
 */
export async function updateQuizVideoProgress(
  quizId: string,
  payload: {
    watch_progress: number;
    chapter_index?: number;
    completed?: boolean;
  }
) {
  const { data } = await api.post<QuizVideoProgress>(
    `/quizzes/${quizId}/video-progress`,
    payload
  );
  return data;
}

// ---- PHASE 4: One-by-One Answer Flow ----

export type AnswerResponse = {
  correct: boolean;
  correct_answer: any;
  // Teaching note shown after answering ("why is this correct"). Null
  // when the admin left it blank.
  explanation?: string | null;
  // Position(s) of the correct option in the SHUFFLED array the client
  // rendered — so we can highlight the right answer during feedback even
  // when the athlete picked a wrong one. number for single, number[] for
  // multi, null for true_false (use `correct_answer` bool there instead).
  correct_position?: number | number[] | null;
  points_earned: number;
  // NEW: how long the athlete took to answer this question (ms),
  // measured server-side from `answer_started_at`. Populated on every
  // answer regardless of whether the quiz has a timer.
  response_ms?: number;
  speed_factor?: number; // 0..1, share of the base points earned
  // F2: only populated on correct answers when the athlete is on a
  // team with peers who have already answered THIS question. Null
  // during first-of-team pass or for non-athlete callers.
  social?: {
    team_peers: number;
    faster_teammates: number;
    slower_teammates: number;
    percentile_team: number;    // 0..100, higher = beat more teammates
    avg_team_ms: number;
    delta_vs_team_avg_ms: number; // positive = I was faster than avg
  } | null;
  next_question_id: string | null;
  // Lives status (present on every answer). out_of_lives ends the
  // attempt early — completed will also be true.
  lives_remaining?: number;
  out_of_lives?: boolean;
  progress: {
    answered: number;
    total: number;
    correct_count: number;
    wrong_count: number;
    total_score: number;
  };
  completed: boolean;
  // Level info on completion (for the XP bar + level-up moment).
  level_after?: {
    xp: number;
    level: number;
    title: string;
    xp_into_level: number;
    xp_for_next: number;
    progress_pct: number;
  } | null;
  leveled_up?: boolean;
  // Points info when completed
  points_awarded?: number;
  points_deducted?: number;
  net_points?: number;
  is_perfect_score?: boolean;
  // First-time perfect-score bonus folded into points_awarded (0 when none),
  // shown as its own line on the result card so the math reconciles.
  perfect_bonus?: number;
  // Split of this run's gross score (base_from_correct + speed_bonus === score):
  // full base points from correct answers, and the speed bonus earned on top.
  base_from_correct?: number;
  speed_bonus?: number;
  // Retake context for the result-screen explanation.
  is_retake?: boolean;
  previous_best?: number;
  gross_this_attempt?: number;
  // F3: badges awarded specifically for this attempt's completion.
  // Empty array when nothing was newly earned. Client renders a
  // celebration on the result screen for each.
  newly_earned_badges?: {
    id: string;
    code: string;
    name: string;
    description: string;
    icon_emoji: string | null;
    icon_image_url: string | null;
    color: string;
  }[];
};

export type AttemptWithDetails = QuizAttempt & {
  correct_count: number;
  wrong_count: number;
  max_score: number;
  quiz: QuizWithQuestions;
};

/**
 * Answer a single question during quiz attempt.
 * For single / multi-choice: pass `selectedPosition` (0-based index
 *   in the shuffled options array the client is rendering).
 * For true_false: pass `answer` (boolean).
 * Response includes response_ms + speed_factor so the client can
 * show "Rapid!" / "Instant!" chips + animate the points earned.
 */
export async function answerQuestion(
  quizId: string,
  attemptId: string,
  questionId: string,
  payload: { selectedPosition?: number; selectedPositions?: number[]; answer?: any }
) {
  const { data } = await api.post<AnswerResponse>(
    `/quizzes/${quizId}/attempts/${attemptId}/answer`,
    {
      question_id: questionId,
      selected_position: payload.selectedPosition,
      selected_positions: payload.selectedPositions,
      answer: payload.answer,
    }
  );
  return data;
}

/**
 * Get attempt details including correct/wrong counts
 */
export async function getAttemptDetails(quizId: string, attemptId: string) {
  const { data } = await api.get<AttemptWithDetails>(
    `/quizzes/${quizId}/attempts/${attemptId}`
  );
  return data;
}

/**
 * Per-quiz funnel + completion stats for the admin dashboard.
 */
export interface QuizStats {
  quiz: { id: string; title: string; reward_points: number };
  views: number;
  started: number;
  completed: number;
  total_attempts: number;
  total_completed_attempts: number;
  avg_score: number;
  total_points_awarded: number;
  schedule_drop_count: number;
  last_completed_at: string | null;
}

export async function getQuizStats(quizId: string): Promise<QuizStats> {
  const { data } = await api.get<QuizStats>(`/quizzes/${quizId}/stats`);
  return data;
}

// ── Athlete: category-first navigation ─────────────────────────────
export type MyQuizCategory = {
  id: string;
  name: string;
  title: string;
  image_url: string | null;
  order_no: number;
  total: number;
  completed: number;
  percent: number;
  new_count: number;
};

export async function getMyQuizCategories(): Promise<MyQuizCategory[]> {
  const { data } = await api.get<MyQuizCategory[]>("/quizzes/categories/me");
  return data;
}

export type CategoryQuiz = {
  id: string;
  title: string;
  description: string | null;
  difficulty: "easy" | "medium" | "hard";
  category: string;
  reward_points: number;
  max_points: number;
  question_count: number;
  video_mode?: QuizVideoMode;
  // "failed" = the last run ended because the athlete ran out of lives (not
  // every question answered): not completed, shown as "Reia".
  status: "not_started" | "in_progress" | "failed" | "completed";
  best_score: number | null;
  attempt_count: number;
  // Sequential theme gating: true when an earlier quiz in the same theme must
  // be finished first. `prerequisite_title` names the quiz to complete.
  locked?: boolean;
  prerequisite_title?: string | null;
  // Whether the quiz is included in the Free plan.
  free_included?: boolean;
  // True when the athlete is on Free and this quiz isn't in Free — visible but
  // not playable until they upgrade.
  plan_locked?: boolean;
  // Theme the quiz belongs to (null = no theme).
  theme_id?: string | null;
};

// A theme inside a category, with the athlete's progress through it.
// `id: null` groups the category's quizzes that have no theme.
export type CategoryTheme = {
  id: string | null;
  title: string | null;
  total: number;
  completed: number;
  percent: number;
};

export type MyCategoryQuizzes = {
  category: { name: string; title: string; image_url: string | null; description: string | null };
  quizzes: CategoryQuiz[];
  themes?: CategoryTheme[];
};

export async function getMyCategoryQuizzes(name: string): Promise<MyCategoryQuizzes> {
  const { data } = await api.get<MyCategoryQuizzes>(`/quizzes/category/${encodeURIComponent(name)}/me`);
  return data;
}

// Regenerating "lives" (hearts) pool for the quiz game.
export type LivesState = {
  lives: number;
  max: number;
  hard_cap: number;
  regen_ms: number;
  ms_to_next_life: number;
  ms_to_full: number;
};

export async function getMyLives(): Promise<LivesState> {
  const { data } = await api.get<LivesState>("/quizzes/lives/me");
  return data;
}
